/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    takeEvery, call, fork, select, put,
} from 'redux-saga/effects';
import personClient from '@wc-api-person/mbp';
import mbpLogger from '@module-logger/mbp';
import mbpUtil from '@api-util/mbp';
import * as addressBookActions from './AddressBook-Actions';
import * as addressBookActionTypes from './AddressBook-ActionTypes';
import { commonSelectors } from '../Common';
import { showLoadingMessageActions } from '../../../Common/ducks/ShowLoadingMessage';
import { getIsWriteScopeCheckout } from '../../../Checkout/ducks/Global/Global-Selectors';
import addressBookServiceClient from '../../../../../apis/address-book-apis/addressBookServiceClient';
import { checkJWT } from '../Auth/Auth-Operations';
import { getBrand } from '../Common/Common-Selectors';

function* workerLoadAddressBook(action) {
    const contactId = yield select(commonSelectors.getContactId);
    try {
        yield put(showLoadingMessageActions.updateShowLoadingMessage());

        let totalEntriesAddress = 0;
        const isWriteScopeCheckout = yield select(getIsWriteScopeCheckout);
        if (contactId && isWriteScopeCheckout) {
            const jwtToken = yield call(checkJWT);

            const payload = {
                wcEnv: {},
                contactId,
                jwtToken,
            };
            const response = yield call(addressBookServiceClient.getAllAddressBookV2, payload);

            if (response.data?.addressBookResponse) {
                const { addressList, totalEntries } = response.data.addressBookResponse;
                const totalPages = 1; // Math.ceil(totalEntries / TOTAL_ENTRIES_PER_PAGE);
                totalEntriesAddress = totalEntries;
                const addressBookList = [];
                addressList.forEach((addressBookItem) => {
                    const addressesGroupByName = addressBookItem.addresses || [];
                    addressesGroupByName.forEach((addressBookRec) => {
                        if (addressBookRec.addressLineOne) {
                            addressBookList.push({
                                ...addressBookItem,
                                addresses: [{ ...addressBookRec }],
                            });
                        }
                    });
                });

                yield put(addressBookActions.saveAddressBook(addressBookList, totalEntries, 0, totalPages));
            } else {
                yield put(addressBookActions.savedAddressBook());
            }
        }

        if (action.data.handleCallback) {
            yield call(action.data.handleCallback, false, totalEntriesAddress);
        }

        yield put(addressBookActions.proccessAddressBookCompleted());
    } catch (ex) {
        const { response } = ex;
        if (
            response
            && response.data
            && response.data.StatusCode
            && response.data.StatusCode.toString() !== '404'
        ) {
            mbpLogger.logError({
                function: 'workerLoadAddressBook',
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                jsError: ex,
                message: `workerLoadAddressBook - Load Address Book Failed - contactId:${contactId}`,
            });
        }
        yield put(addressBookActions.saveAddressBook([], 0, 0, 0));
        yield put(addressBookActions.proccessAddressBookCompleted());
    }
    yield put(showLoadingMessageActions.flagOffShowLoadingMessage());
}

function* workerEditAddressBook(action) {
    const contactId = yield select(commonSelectors.getContactId);

    try {
        const { data } = action;
        const jwtToken = yield call(checkJWT);
        const brandDetails = yield select(getBrand);

        const addressBookEntry = {
            ...data.addressBookEntry,
            addressType: data.addressType || 'Residence',
            brandId: brandDetails.id,
        };
        if (data.addressType) {
            addressBookEntry.addressType = data.addressType;
        }

        const payload = {
            wcEnv: {},
            jwtToken,
            contactId,
            addressBookEntry,
            addressBookEntryId: data.addressBookEntry.addressBookEntryId,
        };
        yield put(showLoadingMessageActions.updateShowLoadingMessage());
        const response = yield call(addressBookServiceClient.updateAddressBookV2, payload);

        if (response.data?.addressResponse) {
            const pageNumber = yield select(commonSelectors.getAddressBookPageNumber);
            const info = {
                data: {
                    pageNumber,
                },
            };
            yield call(workerLoadAddressBook, info);
        }
        yield put(addressBookActions.proccessAddressBookCompleted());
    } catch (ex) {
        mbpLogger.logError({
            action,
            function: 'workerLoadAddressBook',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'workerEditAddressBook - Edit Address Book Failed',
        });
        yield put(addressBookActions.proccessAddressBookCompleted());
    }
    yield put(showLoadingMessageActions.flagOffShowLoadingMessage());
}

function* workerAddAddressBook(action) {
    const contactId = yield select(commonSelectors.getContactId);
    const brandDetails = yield select(getBrand);

    try {
        yield put(showLoadingMessageActions.updateShowLoadingMessage());

        const { data } = action;
        const jwtToken = yield call(checkJWT);

        const payload = {
            wcEnv: {},
            jwtToken,
            contactId,
            addressBookEntry: {
                ...data.addressBookEntry,
                brandId: brandDetails.id,
            },
        };

        const response = yield call(addressBookServiceClient.addAddressBookV2, payload);

        if (response.data?.addressResponse) {
            const pageNumber = yield select(commonSelectors.getAddressBookPageNumber);
            const info = {
                data: {
                    pageNumber,
                },
            };
            yield call(workerLoadAddressBook, info);
        }

        yield put(addressBookActions.proccessAddressBookCompleted());
    } catch (ex) {
        mbpLogger.logError({
            action,
            function: 'workerLoadAddressBook',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: `workerAddAddressBook - Edit Address Book Failed - contactId:${contactId}`,
        });

        yield put(addressBookActions.proccessAddressBookCompleted());
    }
    yield put(showLoadingMessageActions.flagOffShowLoadingMessage());
}

function* workerRemoveAddressBook(action) {
    const contactId = yield select(commonSelectors.getContactId);

    try {
        const jwtToken = yield call(checkJWT);
        const wcEnv = {
            host: mbpUtil.getEnv('APP_WC_HOST'),
        };

        const payload = {
            wcEnv,
            jwtToken,
            contactId,
            addressBookEntryId: action.data.addressBookEntryId,
        };

        yield put(showLoadingMessageActions.updateShowLoadingMessage());
        const response = yield call(addressBookServiceClient.removeAddressBookV2, payload);

        if (response.status === 200) {
            const pageNumber = yield select(commonSelectors.getAddressBookPageNumber);
            const info = {
                data: {
                    pageNumber,
                },
            };
            yield call(workerLoadAddressBook, info);
        }
    } catch (ex) {
        mbpLogger.logError({
            action,
            function: 'workerRemoveAddressBook',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: `workerRemoveAddressBook - Remove Address Book Failed - contactId: ${contactId}`,
        });
    }

    yield put(showLoadingMessageActions.flagOffShowLoadingMessage());
}

function* workerLoadBillingAddress() {
    try {
        const contactId = yield select(commonSelectors.getContactId);
        const accessToken = yield call(checkJWT);
        const wcEnv = {
            host: mbpUtil.getEnv('APP_WC_HOST'),
        };
        const response = yield call(personClient.getBillingAddress, wcEnv, contactId, accessToken);

        if (response.data && response.data.Result) {
            const { billingAddresses } = response.data.Result.billingAddress;
            yield put(addressBookActions.saveBillingAddress(billingAddresses));
        }
        yield put(addressBookActions.savedBillingAddress());
    } catch (ex) {
        const { response } = ex;
        if (response && response.status && response.status.toString() !== '401') {
            mbpLogger.logError({
                function: 'workerLoadBillingAddress',
                appName: process.env.npm_package_name,
                module: 'mbp-member',
                jsError: ex,
                message: 'workerLoadBillingAddress - Load Billing Address Failed',
            });
        }
    }
}

function* watcherLoadAddressBook() {
    yield takeEvery(addressBookActionTypes.LOAD_ADDRESS_BOOK, workerLoadAddressBook);
}

function* watcherAddAddressBook() {
    yield takeEvery(addressBookActions.addAddressBook().type, workerAddAddressBook);
}

function* watcherEditAddressBook() {
    yield takeEvery(addressBookActions.editAddressBook().type, workerEditAddressBook);
}

function* watcherRemoveAddressBook() {
    yield takeEvery(addressBookActionTypes.REMOVE_ADDRESS_BOOK, workerRemoveAddressBook);
}

function* watcherLoadBillingAddress() {
    yield takeEvery(addressBookActionTypes.LOAD_BILLING_ADDRESS, workerLoadBillingAddress);
}

const watchers = [
    fork(watcherLoadAddressBook),
    fork(watcherAddAddressBook),
    fork(watcherEditAddressBook),
    fork(watcherRemoveAddressBook),
    fork(watcherLoadBillingAddress),
];

const workers = {
    workerLoadBillingAddress,
    workerAddAddressBook,
    workerEditAddressBook,
    workerLoadAddressBook,
};

export default {
    watchers,
    workers,
};
